import type { ButtonHTMLAttributes, JSX, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * Кнопки. Что здесь важного:
 *
 *  - на экране ровно один `primary` — остальное подчинено ему;
 *  - у любой кнопки есть press-отклик (`.press`, scale 0.97): без него элемент
 *    кажется неживым, и это первое, что замечают в «дешёвом» интерфейсе;
 *  - `loading` не подменяет текст спиннером, а добавляет его и блокирует
 *    кнопку: подпись должна оставаться читаемой, иначе непонятно, что грузится;
 *  - минимальная высота 36px, у иконочных — 36×36 плюс расширение hit-области
 *    до 44px на тач-устройствах.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-honey text-honey-ink border border-honey hover:bg-honey-hot hover:border-honey-hot shadow-sm',
  secondary: 'bg-panel-2 text-ink border border-line hover:bg-panel-3 hover:border-line-strong',
  ghost: 'bg-transparent text-ink-muted border border-transparent hover:bg-panel-2 hover:text-ink',
  danger: 'bg-transparent text-fail border border-fail/40 hover:bg-fail/12 hover:border-fail'
}

const SIZE: Record<Size, string> = {
  sm: 'h-8 gap-1.5 px-2.5 text-xs',
  md: 'h-9 gap-2 px-3.5 text-base'
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={cn(
        'press inline-flex shrink-0 items-center justify-center rounded-sm font-medium whitespace-nowrap',
        'disabled:cursor-not-allowed disabled:opacity-45',
        VARIANT[variant],
        SIZE[size],
        className
      )}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="size-3.5 animate-spin" /> : null}
      {children}
    </button>
  )
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Обязателен: иконка без подписи невидима для скринридера. */
  label: string
  children: ReactNode
  size?: Size
  tone?: 'default' | 'danger'
}

export function IconButton({
  label,
  children,
  size = 'md',
  tone = 'default',
  className,
  ...rest
}: IconButtonProps): JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'press relative inline-flex shrink-0 items-center justify-center rounded-sm border border-transparent',
        // Псевдоэлемент расширяет область нажатия до 44px, не меняя вид: иконка
        // 32×32 на тач-устройстве иначе требует прицеливания.
        'after:absolute after:-inset-1.5 after:content-[""]',
        size === 'sm' ? 'size-8' : 'size-9',
        tone === 'danger'
          ? 'text-fail hover:bg-fail/12'
          : 'text-ink-muted hover:bg-panel-2 hover:text-ink',
        'disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-transparent',
        className
      )}
      {...rest}
    >
      {children}
    </button>
  )
}
